/**
 * Hook to update the user's email address.
 *
 * This mirrors the mobile app's useUpdateEmail hook from:
 * src/api/mutations/member/useUpdateEmail.ts
 *
 * Note: This only works for non-social login users.
 * Social login users cannot change their email.
 */

import { useQueryClient } from '@tanstack/react-query';
import { useApiMutation } from '@/api/hooks/useMutation';

export interface UpdateMemberEmailRequest {
  email: string;
  password?: string;
}

export interface UpdateMemberEmailResponse {
  email: string;
}

/**
 * Hook to update the current user's email address.
 *
 * Requires the user's current password for verification.
 * Only works for non-social login users.
 *
 * @returns Mutation for updating email
 */
export function useUpdateEmail() {
  const queryClient = useQueryClient();

  return useApiMutation<UpdateMemberEmailResponse, Error, UpdateMemberEmailRequest>({
    mutationFn: (api) => async (request: UpdateMemberEmailRequest) => {
      const response = await api.members.membersControllerUpdateMemberEmail(request);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate user-related queries to refresh the email
      queryClient.invalidateQueries({ queryKey: ['userAttributes'] });
    },
  });
}

export default useUpdateEmail;
